<template>
	<map-view style="display:flex;">
		<LMap
			ref="fleetMap"
			:zoom="7"
			style="position:absolute;"
			:center="[42.09105289033012, -86.3687610129183]"
			:minZoom="5"
			@update:zoom="updateZoom"
		>
			<MapPanControl
				@pan-control-click="handlePan"
				:class="panControlNeedsBuffer ? 'fvu-buffered' : ''"
			></MapPanControl>
			<l-control-layers ref="layerControl"></l-control-layers>
			<l-tile-layer
				v-for="tileProvider in tileProviders"
				:key="tileProvider.name"
				:ref="'tilelayer__' + tileProvider.name"
				:name="tileProvider.name"
				:visible="tileProvider.visible"
				:url="tileProvider.url"
				:options="mergeOptions(tileProvider.options)"
				:attribution="tileProvider.attribution"
				layer-type="base"
				layerType="base"
				@click="$emit('click', $event)"
			></l-tile-layer>
			<l-control-scale
				position="bottomleft"
				:imperial="true"
				:metric="true"
			></l-control-scale>
		</LMap>
	</map-view>
</template>
<script>
import MapView from "@/views/MapView";
import * as L from "leaflet";
import {
	LMap,
	LTileLayer,
	LMarker,
	LControlLayers,
	LControlScale
} from "vue2-leaflet";
import { Component } from "vue-property-decorator";
import MutationTypes from "@/store/mutation-types";
import { EventBus } from "@/event-bus";
import Vue from "vue";
import MapPanControl from "@/components/MapPanControl.vue";
import Service from "./service";
import { Watch } from "vue-property-decorator";

const DEFAULT_ZOOM = 7;
const DEFAULT_MAX_ZOOM = 18;
const DEFAULT_LAYERS_CONTROL_POSITION = "topright";

const mapProps = {
	zoom: {
		default: () => null
	},
	options: {
		default: () => ({
			maxZoom: DEFAULT_MAX_ZOOM,
			panControl: true
		})
	},
	tileLayerOptions: {
		default: () => ({
			maxNativeZoom: DEFAULT_MAX_ZOOM,
			maxZoom: DEFAULT_MAX_ZOOM
		})
	},
	center: {
		default: () => null
	},
	positionKey: {
		type: String,
		default: null
	},
	aggressivePositionSaving: {
		type: Boolean,
		default: false
	},
	rememberPosition: {
		type: Boolean,
		default: false
	},
	layersControlPosition: {
		default: () => DEFAULT_LAYERS_CONTROL_POSITION
	},
	panControlNeedsBuffer: {
		type: Boolean,
		default: false
	}
};

@Component({
	props: mapProps,
	components: {
		LMarker,
		LMap,
		LTileLayer,
		LControlLayers,
		LControlScale,
		MapView,
		L,
		MapPanControl
	}
})
class FleetTracker extends Vue {
	mqttDisconnected = true; //a flag to prevent race conditions between mqtt and the endpoint.
	allWorkZones = []; // contains Id, Name, WellKnownText, Polygon (leaflet object), vehicleIds (vehicles that have entered the work zone.)
	allMarkerLines = []; // contains vehicleId, line (leaflet object), tail, (array of points)
	allMarkers = []; // contains vehicleId, marker (leaflet object), currentVehicleWorkZones[](array of id numbers)
	mapObject = {}; // leaflet map object
	initVehicles = []; // results from endpoint . serialNumber, hardwareModel, vin, organizationAbbreviation, organizationUniqueId, uniqueID, description, year, vehicleMake, vehicleModel, mqttTopic
	focusedVehicle; //the serial number of the vehicle that the camera will fly to.
	focusMarker; //The marker L object that is currently selected
	firstMarkerFrame = false; //used to prevent map clicks from de-selecting while clicking a map-control selection.
	service = new Service(); //API endpoint
	maxTailLength = 100; //The max lengh of a vehicle trailing line.

	get mapZoom() {
		const propZoom = this.zoom;
		let myZoom = this._zoom;
		const savedData = this.savedPositionData;

		if (propZoom) return propZoom; // prop priority
		if (!myZoom) {
			if (!savedData) return DEFAULT_ZOOM;
			myZoom = savedData.zoom;
			this._zoom = myZoom;
		}
		return myZoom;
	}
	get tileProviders() {
		return this.$store.state.map.tileProviders;
	}
	//-----------------------------------------------VUE HOOKS---------------------------------
	mounted() {
		this.mapObject = this.$refs.fleetMap.mapObject;
		this.focusedVehicle = "None";
		this.focusMarker = new L.marker([0, 0], {
			icon: new L.icon({
				iconUrl: "/images/svg/Selection.svg",
				iconSize: [50, 50],
				iconAnchor: [25, 20]
			})
		});
		this.focusMarker.setZIndexOffset(1000);
		this.focusMarker.on(
			"click",
			function() {
				this.markerUnselect();
			},
			this
		);
		this.focusMarker.on("mouseover", function() {
			this.openPopup();
		});
		this.focusMarker.on("mouseout", function() {
			this.closePopup();
		});
		this.mapObject.on(
			"click",
			function() {
				if (this.focusedVehicle != "None" && !this.firstMarkerFrame) {
					this.markerUnselect();
				}
			},
			this
		);
		L.Control.MapOption = L.Control.extend({
			options: { position: "topright" },
			onAdd: function() {
				let div = L.DomUtil.create("div");
				let countLbl = L.DomUtil.create("span");
				let title = L.DomUtil.create("div");
				title.innerHTML = "<strong>Vehicles</strong>";
				countLbl.classList.add("NumberTag");
				countLbl.innerHTML = "0";
				div.appendChild(countLbl);
				div.appendChild(title);
				div.id = "VehicleFilter";
				div.classList.add("MapOption");
				return div;
			},

			onRemove: function() {
				// Nothing to do here
			}
		});
		L.control.mapOption = function() {
			return new L.Control.MapOption();
		};

		L.control.mapOption({ position: "topright" }).addTo(this.mapObject);
	}
	async beforeMount() {
		this.checkMQTTConnection();
		await this.loadVehicles();
	}
	//-----------------------------------------------API CALLS---------------------------------
	async loadVehicles() {
		this.errorMsg = null;
		try {
			let results = await this.service.fetchVehicles();
			if (!results.ok) {
				throw results.errorMessage;
			}
			this.initVehicles = results.data;
		} catch (error) {
			const errorMessage = (error.name || error).toLowerCase();
			if (errorMessage.indexOf("abort") > -1) {
				return;
			}
			console.error(errorMessage);
			this.errorMsg = "Error in loading information";
		}
	}
	/*protects against race conditions (endpoint vs mqtt connection)*/
	@Watch("initVehicles")
	onInitVehicleChange() {
		if (this.mqttDisconnected === false) {
			this.subscribeToAllTopics(this.initVehicles);
		}
	}
	//-----------------------------------------------MAP ITEMS-----------------------------------
	/*plots work zone polygon on map.*/
	plotWorkZone(workZone) {
		var latlngs = [];
		workZone.polyStr = workZone.polyStr.substring(
			workZone.polyStr.indexOf("((") + 1,
			workZone.polyStr.length - 2
		); //remove leading and trailing string junk
		var lnglats = workZone.polyStr.split(","); //split between points
		let halfWay = [];
		for (let i = 0; i < lnglats.length; i++) {
			lnglats[i] = lnglats[i].substring(1, lnglats[i].length);
			var lngLatPoint = lnglats[i].split(" "); //split inner
			var latLngPoint = [lngLatPoint[1], lngLatPoint[0]]; //switch lat and long to work with leaflet polygon
			latlngs.push(latLngPoint);
			if (halfWay.length === 0 && i > lnglats.length / 6) {
				halfWay.push(latLngPoint);
			}
		}
		workZone.marker.setLatLng(halfWay[0]);
		workZone.marker.addTo(this.mapObject);
		workZone.polygon = new L.polygon(latlngs, {
			color: "black",
			fillOpacity: 0.6,
			fillColor: "orange"
		});
		workZone.marker.on("mouseover", function() {
			this.openPopup();
		});
		workZone.marker.on("mouseout", function() {
			this.closePopup();
		});
		workZone.marker.bindPopup(
			"Project: " + workZone.Name + "</br>Lane Closure ID: " + workZone.Id
		);
		workZone.polygon.addTo(this.mapObject);
		return workZone;
	}
	plotMarkerTail(vehicleId, lat, lon) {
		var markerLine = this.allMarkerLines.find(
			item => item.vehicleID === vehicleId
		);
		markerLine.line.unshift([lat, lon]);
		if (markerLine.line.length > this.maxTailLength) {
			markerLine.line.pop([lat, lon]);
		}
		markerLine.tail.setLatLngs(markerLine.line);
	}
	updateMarker(marker, speed, heading, serialNumber, currentPos) {
		this.updateMarkerTooltip(marker, speed, serialNumber);
		if (speed > 0.1) {
			marker.setRotationAngle(heading);
		}
		marker.setLatLng(currentPos);
		return;
	}
	updateMarkerTooltip(marker, speed, serialNumber) {
		let initVehicle = this.initVehicles.find(
			item => item.serialNumber === serialNumber
		);
		var markerMsg =
			"<strong>Vehicle:</strong> " +
			initVehicle.organizationAbbreviation +
			" - ";
		if (initVehicle.organizationUniqueId != null) {
			markerMsg += initVehicle.organizationUniqueId;
		} else {
			markerMsg += initVehicle.uniqueID;
		}
		if (
			initVehicle.year != 0 ||
			initVehicle.vehicleMake != null ||
			initVehicle.vehicleModel != null
		) {
			markerMsg += '<br/>(<span style="color: silver;">';
			if (initVehicle.year != 0) {
				markerMsg += initVehicle.year + " ";
			}
			if (initVehicle.vehicleMake != null) {
				markerMsg += initVehicle.vehicleMake + " ";
			}
			if (initVehicle.vehicleModel != null) {
				markerMsg += initVehicle.vehicleModel;
			}
			markerMsg += "</span>)";
		}
		markerMsg +=
			"<br/><strong>Description:</strong> " + initVehicle.description;
		markerMsg += "<br/><strong>VIN</strong>: " + initVehicle.vin;
		markerMsg += "<br/><strong>Speed:</strong> " + Math.round(speed);
		marker.bindPopup(markerMsg);
		return;
	}
	deleteWorkZone(workZone) {
		workZone.polygon.remove();
		workZone.marker.remove();
		this.allWorkZones = this.allWorkZones.filter(
			item => item.Id != workZone.Id
		);
	}
	expireAllWorkZone(serialNumber) {
		var expireWorkZones = this.allWorkZones.filter(
			item => item.lastVehicle === serialNumber
		);
		for (let i = 0; i < expireWorkZones.length; i++) {
			switch (expireWorkZones[i].expireCountdown) {
				case -1:
					expireWorkZones[i].expireCountdown = this.maxTailLength - 3;
					break;
				case 0:
					this.deleteWorkZone(expireWorkZones[i]);
					break;
				default:
					expireWorkZones[i].expireCountdown--; //decriment
			}
		}
	}
	//---------------------------------------------MQTT-------------------------------------------
	/*protects against race conditions (endpoint vs mqtt connection)*/
	checkMQTTConnection() {
		EventBus.onGlobalEvent("mqtt-client:isConnected", ({ isConnected }) => {
			if (isConnected && this.mqttDisconnected) {
				this.mqttDisconnected = false;
				EventBus.onGlobalEvent(
					"mqtt-client:messageArrived",
					this.onMessageRecieved
				);
				if (this.initVehicles.length > 0) {
					this.subscribeToAllTopics(this.initVehicles);
				}
			}
		});
	}
	/*loops through all possible vehicles and mqtt subscribes individually*/
	subscribeToAllTopics(topics) {
		for (let i = 0; i < topics.length; i++) {
			let topic = {
				subscription: topics[i].mqttTopic,
				name: "fleetTracker_" + topics[i].uniqueID
			};
			this.$store.commit(MutationTypes.MQTT_SUBSCRIBE, topic);
		}
	}
	/*ensure that only correct topics are acted upon.*/
	onMessageRecieved(payload) {
		if (payload.message.topic.startsWith("v1/geofenceDetection")) {
			this.onMessageReceivedGeoFence(payload);
		}
	}
	/*MQTT message received from vehicle*/
	onMessageReceivedGeoFence(payload) {
		const payloadObj = JSON.parse(payload.message.payloadString);
		const lat = payloadObj.GpsLocationData.GpsData.Latitude;
		const lon = payloadObj.GpsLocationData.GpsData.Longitude;
		const speed = payloadObj.GpsLocationData.GpsData.Speed;
		const heading = payloadObj.GpsLocationData.GpsData.Heading;
		const serialNumber = payloadObj.SerialNumber + "" + payloadObj.Hardware; //ensure unique identifier if different hardware providers have overlapping serial numbers.
		const workZones = payloadObj.GeofenceInfo.filter(
			item => item.GeofenceType === "WorkZone"
		);
		var point = { lon, lat };
		var oldMarker = this.allMarkers.filter(
			obj => obj.vehicleID === serialNumber
		);
		this.expireAllWorkZone(serialNumber);
		this.markerSelectionUpdate(point, serialNumber, speed);
		this.firstMarkerFrame = false;
		for (let i = 0; i < workZones.length; i++) {
			var existingWorkZone = this.allWorkZones.find(
				item => item.Id === workZones[i].Id
			);
			//if work zone not listed
			if (!this.allWorkZones.some(item => item.Id === workZones[i].Id)) {
				//make record of work zone
				var newWorkZone = {
					Id: workZones[i].Id,
					Name: workZones[i].Name,
					polyStr: workZones[i].WellKnownText,
					polygon: new L.Polygon([]),
					VehicleIds: [serialNumber],
					expireCountdown: -1,
					lastVehicle: "",
					marker: new L.marker(
						{ lon: 0, lat: 0 },
						{
							icon: new L.icon({
								iconUrl: "/images/barrelorangestripe.png",
								iconSize: [15, 23]
							})
						}
					)
				};

				//draw work zone
				newWorkZone = this.plotWorkZone(newWorkZone);
				this.allWorkZones.push(newWorkZone);
			} else if (!existingWorkZone.VehicleIds.includes(serialNumber)) {
				//if vehicle not found in existing work zone
				existingWorkZone.VehicleIds.push(serialNumber);
				existingWorkZone.expireCountdown = -1; //stops any countdown
				existingWorkZone.lastVehicle = "";
			}
		}
		//if not in a work zone:
		if (workZones.length === 0) {
			//find all work zones where this vehicle still exists
			const workZonesToDelete = this.allWorkZones.filter(
				item => item.VehicleIds[0] === serialNumber
			);
			for (let i = 0; i < workZonesToDelete.length; i++) {
				//remove vehicle from work zones
				workZonesToDelete[i].VehicleIds = workZonesToDelete[
					i
				].VehicleIds.filter(item => item != serialNumber);
				//if work zone empty
				if (workZonesToDelete[i].VehicleIds.length === 0) {
					workZonesToDelete[i].lastVehicle = serialNumber;
				}
			}
		}
		//if exists marker exists
		if (oldMarker.length > 0) {
			this.updateMarker(
				oldMarker[0].marker,
				speed,
				heading,
				serialNumber,
				point
			);
			this.plotMarkerTail(serialNumber, lat, lon);
			return; //prevent further execution
		}
		//first-time marker setup
		var marker = L.marker(point, {
			icon: new L.icon({
				iconUrl: "/images/svg/BlueTopDownTruck.svg",
				iconSize: [25, 40],
				iconAnchor: [12.5, 10]
			})
		});
		var tail = L.polyline([[lat, lon]]);
		this.updateMarker(marker, speed, heading, serialNumber, point);
		marker.setRotationAngle(heading); //sets initial rotation heading. Will be preserved if vehicle initializes while stopped.
		marker.on("mouseover", function() {
			this.openPopup();
		});
		marker.on("mouseout", function() {
			this.closePopup();
		});
		marker.on(
			"click",
			function(e) {
				this.markerSelectionInit(e.latlng, serialNumber, speed);
			},
			this
		);
		//create line object and add to class list
		this.allMarkerLines.push({
			vehicleID: serialNumber,
			line: [[lat, lon]],
			tail
		});
		//create marker object and add to class list
		this.allMarkers.push({
			vehicleID: serialNumber,
			marker,
			currentVehicleWorkZones: []
		});
		marker.addTo(this.mapObject);
		tail.addTo(this.mapObject);

		//Add new item to vehicle list
		this.vehicleOptions(
			this.mapObject._controlCorners.topright.children[0],
			serialNumber,
			speed
		);
	}
	//---------------------------------------------FOCUS FUNCTION-----------------------------------
	/*First-time selection marker setup. This is hit when a vehicle is selected directly or from the list.*/
	markerSelectionInit(point, serialNumber, speed) {
		if (this.focusedVehicle != serialNumber) {
			this.firstMarkerFrame = true;
			this.focusedVehicle = serialNumber;
			this.mapObject.dragging.disable();
			this.mapObject.scrollWheelZoom = false;
			this.focusMarker.addTo(this.mapObject);
			this.markerSelectionUpdate(point, serialNumber, speed);
			this.mapObject.setZoom(18);
		} else {
			this.markerUnselect();
		}
	}
	/*Move selection marker to new position and update the tooltip*/
	markerSelectionUpdate(point, serialNumber, speed) {
		if (this.focusedVehicle === serialNumber) {
			let zoomLvl = this.mapObject.getZoom(); //needed to preserve zoom level
			this.focusMarker.setLatLng(point);
			this.mapObject.setView(point, zoomLvl, { animate: false });
			this.updateMarkerTooltip(this.focusMarker, speed, serialNumber);
		}
	}
	markerUnselect() {
		this.focusedVehicle = "None";
		this.focusMarker.remove();
		this.mapObject.dragging.enable();
	}
	//---------------------------------------------MAP CONTROLS-----------------------------------
	updateCenter(c) {
		this._center = c;
		this.$emit("update:center", c);
		if (this.aggressivePositionSaving) this.savePosition();
	}
	updateZoom(z) {
		this._zoom = z;
		this.$emit("update:zoom", z);
		if (this.aggressivePositionSaving) this.savePosition();
	}
	mergeOptions(options) {
		return Object.assign({}, this.tileLayerOptions, options);
	}
	handlePan(directionIndex) {
		try {
			const mo = this.mapObject;
			mo.panTo(this.generatePanPoint(directionIndex)); // move the center to there
		} catch (e) {
			console.error("failed to move map", e);
		}
	}
	/*Adds all active vehicles to a custom control vehicle list and handles click function for each.*/
	vehicleOptions(container, serialNum, speed) {
		let initVehicle = this.initVehicles.find(
			item => item.serialNumber === serialNum
		);
		let point = this.allMarkers
			.find(item => item.vehicleID === serialNum)
			.marker.getLatLng();
		let serialNumber = serialNum;
		if (initVehicle.organizationUniqueId != null) {
			serialNumber =
				initVehicle.organizationAbbreviation +
				" - " +
				initVehicle.organizationUniqueId;
		}
		let newElement = L.DomUtil.create("div");
		let btn = L.DomUtil.create("INPUT");
		btn.classList.add("VehicleLink");
		btn.setAttribute("type", "button");
		btn.setAttribute("value", serialNumber);
		btn.classList.add("goToBtn");
		var self = this;
		let onClickHandler = function() {
			let latestPoint = point;
			self.markerSelectionInit(latestPoint, serialNum, speed);
		};
		btn.addEventListener("click", onClickHandler);
		newElement.appendChild(btn);
		container.appendChild(newElement);
		container.children[0].innerHTML = this.allMarkers.length;
	}
}

export default FleetTracker;
export { mapProps };
</script>
<style>
.fleet-tracker {
	display: flex;
}
.fleet-tracker .button {
	font-size: small;
}
.fleet-tracker .leaflet-popup .leaflet-zoom-animated {
	width: fit-content;
}
.fleet-tracker .leaflet-bottom {
	margin: 2em;
}
.fleet-tracker nav {
	margin: 0 1em;
	margin-bottom: 1em;
}
.MapOption {
	width: 50px;
	height: 50px;
	background: #fff;
	border: 2px solid rgba(0, 0, 0, 0.2);
	border-radius: 5px;
	background-size: contain;
	background-clip: padding-box;
}
#VehicleFilter {
	background-image: url("/images/svg/MapControlVehicle.svg");
}
.NumberTag {
	color: #fff;
	background-color: darkred;
	width: 20px;
	float: right;
	border-radius: 100%;
}
#VehicleFilter:hover {
	height: unset;
	width: unset;
	background-image: none;
	padding: 10px;
}
#VehicleFilter div {
	display: none;
}
#VehicleFilter:hover .NumberTag {
	display: none;
}
#VehicleFilter:hover div {
	display: block;
	text-align: left;
	list-style: none;
	font-size: 0.9em;
}
.VehicleLink {
	background-color: white !important;
	border: none;
	text-decoration: underline;
	color: blue;
}
</style>
